import {
  useLayoutEffect,
  useState,
  useRef,
  forwardRef,
  type ChangeEvent,
} from "react";
import cx from "classnames";

import { SelectCardGroup } from "./SelectCardGroup";
import { Card, CardProps } from "../Card";
import { Radio, RadioProps } from "../Radio/internal/Radio";
import { Checkbox, CheckboxProps } from "../Checkbox/internal/Checkbox";
import { useOptionallyControlledState } from "../../internal/hooks";
import { useSelectCardContext } from "./internal/SelectCardContext";
import { Icon } from "../Icon";

import CheckboxOutline from "@servicetitan/hammer-icon/mdi/round/check_box_outline_blank.svg";
import CheckboxChecked from "@servicetitan/hammer-icon/mdi/round/check_box.svg";
import RadioOutline from "@servicetitan/hammer-icon/mdi/two-tone/radio_button_unchecked.svg";
import RadioChecked from "@servicetitan/hammer-icon/mdi/two-tone/radio_button_checked.svg";

import styles from "./SelectCard.module.scss";

/**
 * Props for the SelectCard component
 * @extends Omit<CardProps, "onChange" | "id">
 */
export interface SelectCardProps extends Omit<CardProps, "onChange" | "id"> {
  /**
   * Passes props to the underlying `<Checkbox />` component.
   */
  checkboxProps?: Omit<CheckboxProps, "label">;

  /**
   * Controlled state. When provided, the component becomes controlled.
   */
  checked?: boolean;

  /**
   * Uncontrolled initial state. Ignored when inside a `SelectCard.Group` that sets `defaultValue`.
   */
  defaultChecked?: boolean;

  /**
   * When `true`, disables the card.
   */
  disabled?: boolean;

  /**
   * When `true`, sets error styling on the card.
   */
  errored?: boolean;

  /**
   * A unique identifier for the card, used to track the selection state.
   */
  id: string | number;

  /**
   * Function called on selection state change.
   */
  onChange?: (value: boolean) => void;

  /**
   * Passes props to the underlying `<Radio />` component.
   */
  radioProps?: Omit<RadioProps, "label">;

  /**
   * Removes the drop shadow effect on the card.
   */
  removeDropShadow?: boolean;

  /**
   * When `true`, displays a checkbox or radio indicator in a separate column on the left side of the card.
   * The indicator type (checkbox or radio) is automatically determined by the `selectionMode` prop on the
   * `SelectCard.Group`, and defaults to checkbox if no `SelectCard.Group` is used.
   * @default false
   */
  showSelectIndicator?: boolean;
}

/**
 * SelectCard component for creating interactive card-based selection interfaces.
 *
 * Features:
 * - Supports both single selection (radio) and multiple selection (checkbox) modes
 * - Automatic context detection for selection behavior
 * - Visual feedback for selected, disabled, and error states
 * - Accessible with proper ARIA roles and keyboard navigation
 * - Customizable styling through Card component props
 * - Hover and focus states with smooth transitions
 * - Integration with SelectCardGroup for grouped selection
 *
 * @example
 * <SelectCard
 *   id="option-1"
 *   onChange={(checked) => console.log('Selected:', checked)}
 * >
 *   <Text variant="headline" size="small">Option 1</Text>
 *   <Text>Description of option 1</Text>
 * </SelectCard>
 */
export const SelectCard = Object.assign(
  forwardRef<HTMLInputElement, SelectCardProps>(
    function SelectCardInner(props, ref) {
      const {
        checkboxProps,
        children,
        checked: checkedProp,
        className,
        defaultChecked: defaultCheckedProp,
        disabled,
        errored,
        radioProps,
        onChange,
        removeDropShadow,
        showSelectIndicator = false,
        id,
        ...rest
      } = props;

      const [checked, setChecked] = useOptionallyControlledState<boolean>({
        controlledValue: checkedProp,
        defaultValue: defaultCheckedProp || false,
        onChange,
      });

      const context = useSelectCardContext();
      const isCardControlled = checkedProp !== undefined;
      const isGroupControlled = context?.isGroupControlled ?? false;

      // we need to re-render after setting the initial selection state on context
      const [_, setForceRenderCount] = useState(0);
      useLayoutEffect(() => {
        // Sync initial checked state to context (for both controlled and uncontrolled modes).
        // Skip when group is controlled — the group's value prop owns the selection Set.
        const initialChecked = checkedProp ?? defaultCheckedProp;
        if (initialChecked && !isGroupControlled && !context?.hasGroupDefault) {
          context?.onChange?.(
            undefined,
            {
              id,
              checked: initialChecked,
            },
            { internal: true },
          );

          setForceRenderCount((prev) => prev + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      // Sync controlled state changes to context (skip initial mount - handled by above effect)
      const isFirstRender = useRef(true);
      useLayoutEffect(() => {
        if (isFirstRender.current) {
          isFirstRender.current = false;
          return;
        }
        if (isCardControlled && context) {
          // In single selection mode, only sync when checked is true.
          // Unchecking is implicit when another card becomes checked.
          // Syncing checked=false would clear the entire selection.
          if (context.selectionMode === "single" && !checkedProp) {
            return;
          }
          context.onChange?.(
            undefined,
            {
              id,
              checked: checkedProp!,
            },
            { internal: true },
          );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [checkedProp]);

      const handleChange = (event?: ChangeEvent<HTMLInputElement>) => {
        const newChecked = event ? event.target.checked : false;
        setChecked(newChecked);
        context?.onChange?.(event, {
          id,
          checked: newChecked,
        });
      };

      // A selection in the group context supersedes the normal checked state of the card
      const isChecked = context ? context.selection.has(id) : checked;
      const showIndicator = context?.showSelectIndicator || showSelectIndicator;

      // When group is controlled (value prop), drive input from context selection.
      // When group has defaultValue, it takes precedence — ignore individual defaultChecked.
      const inputChecked = isGroupControlled
        ? isChecked
        : isCardControlled
          ? checkedProp
          : undefined;
      const inputDefaultChecked =
        isGroupControlled || isCardControlled
          ? undefined
          : context?.hasGroupDefault
            ? context.selection.has(id)
            : defaultCheckedProp;

      const contentClassName = cx(styles["select-card"], className, {
        [styles["select-card--remove-drop-shadow"]]: removeDropShadow,
        [styles["select-card--checked"]]: isChecked,
        [styles["select-card--selected"]]: isChecked && !errored,
        [styles["select-card--errored"]]: errored,
        [styles["select-card--disabled"]]: disabled,
      });

      return (
        <>
          {(!context || context?.selectionMode === "multiple") && (
            <Checkbox
              {...checkboxProps}
              className={cx(
                styles["select-card-wrapper"],
                checkboxProps?.className,
              )}
              labelWrapperClassName={styles["select-card-label"]}
              checked={inputChecked}
              defaultChecked={inputDefaultChecked}
              onChange={handleChange}
              disabled={disabled}
              ref={ref}
              label={
                <Card
                  {...rest}
                  className={contentClassName}
                  flex={showIndicator ? "1" : undefined}
                  gap={showIndicator ? "2" : undefined}
                  alignItems={showIndicator ? "flex-start" : undefined}
                >
                  {showIndicator ? (
                    <>
                      <span className={styles["icon-wrapper"]} aria-hidden>
                        <Icon
                          size="large"
                          svg={CheckboxChecked}
                          className={styles["icon-checked"]}
                        />

                        <Icon
                          size="large"
                          svg={CheckboxOutline}
                          className={styles["icon-unchecked"]}
                        />
                      </span>
                      {children}
                    </>
                  ) : (
                    children
                  )}
                </Card>
              }
              hideCheckbox
            />
          )}
          {context?.selectionMode === "single" && (
            <Radio
              {...radioProps}
              className={cx(
                styles["select-card-wrapper"],
                radioProps?.className,
              )}
              labelWrapperClassName={styles["select-card-label"]}
              checked={inputChecked}
              defaultChecked={inputDefaultChecked}
              onChange={handleChange}
              disabled={disabled}
              ref={ref}
              name={context.radioName}
              label={
                <Card
                  {...rest}
                  className={contentClassName}
                  flex={showIndicator ? "1" : undefined}
                  gap={showIndicator ? "2" : undefined}
                  alignItems={showIndicator ? "flex-start" : undefined}
                >
                  {showIndicator ? (
                    <>
                      <span className={styles["icon-wrapper"]} aria-hidden>
                        <Icon
                          size="large"
                          svg={RadioOutline}
                          className={styles["icon-unchecked"]}
                        />

                        <Icon
                          size="large"
                          svg={RadioChecked}
                          className={styles["icon-checked"]}
                        />
                      </span>
                      {children}
                    </>
                  ) : (
                    children
                  )}
                </Card>
              }
              hideRadio
            />
          )}
        </>
      );
    },
  ),
  {
    /**
     * SelectCardGroup component for managing groups of selectable cards.
     *
     * Features:
     * - Manages selection state for multiple SelectCard components
     * - Supports both single and multiple selection modes
     * - Provides proper accessibility with legends and ARIA labels
     * - Handles required field validation
     * - Automatic radio button grouping for single selection
     * - Context provider for child SelectCard components
     *
     * @example
     * <SelectCard.Group
     *   legend="Choose your plan"
     *   selectionMode="single"
     *   onChange={(e, state) => console.log('Selection changed:', state)}
     * >
     *   <SelectCard id="basic" onChange={() => {}}>
     *     <Text variant="headline" size="small">Basic Plan</Text>
     *   </SelectCard>
     *   <SelectCard id="premium" onChange={() => {}}>
     *     <Text variant="headline" size="small">Premium Plan</Text>
     *   </SelectCard>
     * </SelectCard.Group>
     */
    Group: SelectCardGroup,
  },
);
SelectCard.displayName = "SelectCard";
